import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { CtaButton } from '@web/molecules';
import { Heading, Eyebrow } from '@web/atoms';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { useReporter } from '../../reporter';
import { EmailCapture } from '../EmailCaptureBar/EmailCapture';
import { SimpleCalloutCard } from '../SimpleCalloutCard';
import { simpleCalloutCardsButtonClicked } from './events';
import {
  StyledSimpleCalloutCards,
  CardWrapper,
  HeadlineWrapper,
  CtaWrapper,
  InputWrapper,
  ButtonWrapper,
  Subheader,
} from './styles';

export const SimpleCalloutCards = ({
  headline,
  eyebrow,
  subheadline,
  cards,
  cta,
  backgroundColor,
  fontColor,
  ctaColor,
  mobileCardsPerRow = 1,
  desktopCardsPerRow = 2,
  typography = 'heading3',
}) => {
  const reporter = useReporter();
  const { formattedAmount } = useShopifyOfferContext();
  return (
    <StyledSimpleCalloutCards $bgColor={backgroundColor} $fontColor={fontColor}>
      <HeadlineWrapper $fontColor={fontColor}>
        {eyebrow && (
          <Eyebrow textAlign={'center'} fontColor={fontColor} marginBottom={0}>
            {interpolate(eyebrow, { offer: formattedAmount ?? '50%' })}
          </Eyebrow>
        )}
        <Heading
          headingLevel="h2"
          typography={typography}
          headingText={interpolate(headline, {
            offer: formattedAmount ?? '50%',
          })}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
          position="center"
          verticalPadding={0}
          horizontalPadding={0}
        />
        {subheadline && (
          <Subheader $fontColor={fontColor}>
            {interpolate(subheadline, { offer: formattedAmount ?? '50%' })}
          </Subheader>
        )}
      </HeadlineWrapper>
      <CardWrapper
        mobileCardsPerRow={mobileCardsPerRow}
        desktopCardsPerRow={desktopCardsPerRow}
      >
        {cards.map((card, index) => (
          <SimpleCalloutCard
            key={index}
            {...card}
            backgroundColor={backgroundColor}
            align={
              desktopCardsPerRow === 2
                ? index % 2 === 0
                  ? 'end'
                  : 'start'
                : 'center'
            }
          />
        ))}
      </CardWrapper>
      {cta && cta.component === 'email_capture' ? (
        <InputWrapper>
          <EmailCapture {...cta} buttonVariant={ctaColor} />
        </InputWrapper>
      ) : (
        <CtaWrapper>
          <ButtonWrapper $bgColor={backgroundColor}>
            <CtaButton
              variant={ctaColor}
              href={cta.target}
              onClick={() =>
                reporter.tag(
                  simpleCalloutCardsButtonClicked(cta.text, cta.target),
                )
              }
            >
              {cta.text}
            </CtaButton>
          </ButtonWrapper>
        </CtaWrapper>
      )}
    </StyledSimpleCalloutCards>
  );
};

SimpleCalloutCards.propTypes = {
  headline: PropTypes.string.isRequired,
  eyebrow: PropTypes.string,
  subheadline: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string.isRequired,
      }).isRequired,
      heading: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      headingLevel: PropTypes.string,
      icon: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
          .isRequired,
        width: PropTypes.string,
        height: PropTypes.string,
      }),
    }),
  ).isRequired,
  cta: PropTypes.shape({
    text: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    buttonVariant: PropTypes.string,
  }),
  fontColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  ctaColor: PropTypes.string,
  typography: PropTypes.string,
};
